import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, ArrowLeft, ChevronRight } from "lucide-react";

export default function KnowledgeCenter() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (slug) {
      api.get(`/knowledge/${slug}`).then((r) => setArticle(r.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
    } else {
      api.get(`/knowledge`).then((r) => setArticles(r.data)).catch(() => setArticles([])).finally(() => setLoading(false));
    }
  }, [slug]);

  if (slug) {
    if (loading) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#003DA5] border-t-transparent rounded-full animate-spin" /></div>;
    if (!article) return <div className="py-20 text-center text-slate-600">Article not found. <Link to="/knowledge" className="text-[#003DA5] underline">Back</Link></div>;
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-white py-12" data-testid="knowledge-article">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/knowledge" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#003DA5] mb-6">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <span className="inline-block px-3 py-1 rounded-full bg-[#E6F0FF] text-[#003DA5] text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
            Knowledge Center
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">{article.title}</h1>
          <p className="mt-3 text-lg text-slate-600">{article.summary}</p>
          <div className="mt-8 prose prose-slate max-w-none whitespace-pre-line text-slate-700 leading-relaxed">
            {article.content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-12" data-testid="knowledge-list">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center max-w-2xl mx-auto">
          <span className="inline-block px-3 py-1 rounded-full bg-[#E6F9F0] text-[#00A86B] text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
            Knowledge Center
          </span>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Motor insurance, decoded.
          </h1>
          <p className="text-slate-600 mt-2">Practical guides on filing claims, surveys, settlements and avoiding rejection.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/knowledge/${a.slug}`}
              className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#003DA5] hover-lift group"
              data-testid={`article-${a.slug}`}
            >
              <BookOpen className="w-6 h-6 text-[#003DA5] mb-3" />
              <h3 className="font-heading font-bold text-lg text-slate-900 group-hover:text-[#003DA5] transition-colors">{a.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{a.summary}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#003DA5]">
                Read article <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
